import type { Router } from 'vue-router';

import { getJobDetailsUrl } from '@/apps/provider/jobs/details/jobDetailsUrl';
import { sendSegmentEvent } from '@/lib/segment';

export type ShareJobLinkResult = 'copied' | 'error';

export type ParentJobLinkShareSource = 'seeker_jobs_open' | 'dashboard_jobs_module';

export const JOB_LINK_COPIED_TOAST = 'Job link copied. Paste to share with a sitter.';
export const JOB_LINK_COPY_ERROR_TOAST = 'Error copying link. Please try again.';

/**
 * Maps a copy outcome to parent-facing toast copy.
 */
export function toastMessageForShareJobLinkResult(result: ShareJobLinkResult): string | null {
  if (result === 'copied') return JOB_LINK_COPIED_TOAST;
  if (result === 'error') return JOB_LINK_COPY_ERROR_TOAST;
  return null;
}

/**
 * Copies the sitter-facing job details URL to the clipboard.
 */
export async function shareOrCopyJobLink(options: {
  jobId: string;
  title?: string;
  router: Pick<Router, 'resolve'>;
}): Promise<ShareJobLinkResult> {
  const url = getJobDetailsUrl(options.jobId, options.router);

  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'error';
  }
}

/**
 * Parent Manage Job orchestration: copy the sitter-facing job URL, track analytics, and return toast copy.
 *
 * The clipboard write starts first so it keeps the tap's user activation, but the event is sent
 * without waiting for it to settle: mobile browsers can leave `writeText` pending after the copy
 * lands, which stranded `Parent Job Link Shared` on mobile web while desktop reported it fine.
 */
export async function shareParentJobLink(options: {
  jobId: string;
  title?: string;
  source: ParentJobLinkShareSource;
  router: Pick<Router, 'resolve'>;
}): Promise<{ result: ShareJobLinkResult; toastMessage: string | null }> {
  const copy = shareOrCopyJobLink({
    jobId: options.jobId,
    title: options.title,
    router: options.router,
  });

  sendSegmentEvent('Parent Job Link Shared', {
    job_id: options.jobId,
    source: options.source,
  });

  const result = await copy;

  return {
    result,
    toastMessage: toastMessageForShareJobLinkResult(result),
  };
}
